// src/services/authService.js
import { auth } from '../firebase';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.authStateListeners = [];
    
    // Initialize auth state listener
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
      this.authStateListeners.forEach(callback => callback(user));
    });
  }

  // Sign in with email and password
  async signIn(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if user is admin (you can customize this logic)
      const isAdmin = await this.isAdmin(user);
      
      if (!isAdmin) {
        await this.signOut();
        return { 
          success: false, 
          error: 'Access denied. Admin privileges required.' 
        };
      }
      
      return { 
        success: true, 
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          isAdmin: true
        }
      };
    } catch (error) {
      console.error('Error signing in:', error);
      
      let errorMessage = 'An error occurred during sign in.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection.';
          break;
        default:
          errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    }
  }

  // Sign out
  async signOut() {
    try {
      await signOut(auth);
      this.currentUser = null;
      return { success: true };
    } catch (error) {
      console.error('Error signing out:', error);
      return { success: false, error: error.message };
    }
  }

  // Get current user
  getCurrentUser() {
    return this.currentUser;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return this.currentUser !== null;
  }

  // Check if user is admin
  async isAdmin(user) {
    if (!user) return false;
    
    // For now, we'll use a simple email-based check
    // In production, you might want to store admin roles in Firestore
    const adminEmails = [
      'admin@digisofts.com',
      'digisofts@gmail.com',
      // Add more admin emails as needed
    ];
    
    return adminEmails.includes(user.email);
  }

  // Check if current user is admin
  async isCurrentUserAdmin() {
    if (!this.currentUser) return false;
    return this.isAdmin(this.currentUser);
  }

  // Add auth state change listener
  onAuthStateChanged(callback) {
    this.authStateListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.authStateListeners.indexOf(callback);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }

  // Send password reset email
  async sendPasswordReset(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error('Error sending password reset:', error);
      
      let errorMessage = 'An error occurred while sending password reset email.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address.';
          break;
        default:
          errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    }
  }

  // Update user profile
  async updateUserProfile(displayName, photoURL) {
    try {
      if (!this.currentUser) {
        return { success: false, error: 'No user is currently signed in.' };
      }
      
      await updateProfile(this.currentUser, {
        displayName: displayName,
        photoURL: photoURL
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user display name or email
  getUserDisplayName() {
    if (!this.currentUser) return '';
    return this.currentUser.displayName || this.currentUser.email;
  }

  // Session timeout management
  setupSessionTimeout(timeoutMinutes = 30) {
    let timeoutId;
    
    const resetTimeout = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (this.isAuthenticated()) {
        timeoutId = setTimeout(() => {
          this.signOut();
          // You can dispatch a custom event or call a callback here
          window.dispatchEvent(new CustomEvent('sessionTimeout'));
        }, timeoutMinutes * 60 * 1000);
      }
    };
    
    // Reset timeout on user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetTimeout, true);
    });
    
    // Initial timeout setup
    resetTimeout();
    
    // Return cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      events.forEach(event => {
        document.removeEventListener(event, resetTimeout, true);
      });
    };
  }
}

export default new AuthService();